import React, { ReactElement, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Form } from 'react-bootstrap'
import CommunityListRow from '~/pages/community/CommunityListRow'
import '@css/pages/community/CommunityTable.scss'
import searchIcon from '@web/images/community/icon-search.svg'
import { useCommunities } from '~/contexts/CommunitiesContext'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import { useStickyState } from '~/common/hooks/useStickyState'
import searchClear from '@web/images/community/search-clear.svg'
import { CommunityListTab } from '~/pages/community/CommunityListLayout'
import { useAuth } from '@web/js/auth/Auth'
import { debounce, sortCommunityList } from '@web/js/utils'
import moreIcon from '@web/images/profile/more-icon.svg'
import ToastComponent from '~/common/ToastComponent'
import { CommunityListViewModel, LocalStorageKey } from '~/types'
import { MembershipAction } from '~/common/JoinCommunityButton'
import { elementClicked, searchQueryLogger } from '~/common/EventLogger'

const COMMUNITY_LIST_MY_SEARCH_KEY = 'CommunityListMySearchKey'
const COMMUNITY_LIST_ALL_SEARCH_KEY = 'CommunityListAllSearchKey'
const COMMUNITY_LIST_VEEVA_ONLY_SEARCH_KEY = 'CommunityListVeevaOnlySearchKey'

enum CommunitySortType {
  NAME,
  TYPE,
  MEMBERS,
  ACTIVITY,
  ACTION,
  CUSTOM,
}

const nameCompare = (a: CommunityListViewModel, b: CommunityListViewModel) => a.name.localeCompare(b.name)

const logJoinClicked = (comm: CommunityListViewModel, action: MembershipAction, e: SyntheticEvent) => {
  if (action == MembershipAction.join) {
    elementClicked(e, 'click-community-join', { communityId: comm.communityId })
  } else {
    elementClicked(e, 'click-community-leave', { communityId: comm.communityId })
  }
}

const CommunityList = ({ communityListTab }: { communityListTab: CommunityListTab }) => {
  const { isVeevan } = useAuth()
  const [sortAscAll, setSortAscAll] = useState<boolean>(true)
  const [sortTypeAll, setSortTypeAll] = useState<CommunitySortType>(CommunitySortType.NAME)
  const [sortAscMine, setSortAscMine] = useState<boolean>(true)
  const [sortAscVeevaOnly, setSortAscVeevaOnly] = useState<boolean>(true)
  const [sortTypeMine, setSortTypeMine] = useState<CommunitySortType>(CommunitySortType.CUSTOM)
  const [sortTypeVeevaOnly, setSortTypeVeevaOnly] = useState<CommunitySortType>(CommunitySortType.CUSTOM)
  const [displayedCountMine, setDisplayedCountMine] = useState<number>(() =>
    Number.parseInt(localStorage.getItem(LocalStorageKey.displayedCountMine) ?? '25')
  )
  const [displayedCountAll, setDisplayedCountAll] = useState<number>(() =>
    Number.parseInt(localStorage.getItem(LocalStorageKey.displayedCountAll) ?? '25')
  )
  const [displayedCountVeevaOnly, setDisplayedCountVeevaOnly] = useState<number>(() =>
    Number.parseInt(localStorage.getItem(LocalStorageKey.displayedCountVeevaOnly) ?? '25')
  )
  const [toastElements, setToastElements] = useState<ReactElement[]>([])
  const [scrolled, setScrolled] = useState(false)

  const { isCondensedPortrait } = useWindowSize()
  const [mySearchText, setMySearchText] = useStickyState('', COMMUNITY_LIST_MY_SEARCH_KEY)
  const [allSearchText, setAllSearchText] = useStickyState('', COMMUNITY_LIST_ALL_SEARCH_KEY)
  const [veevaOnlySearchText, setVeevaOnlySearchText] = useStickyState('', COMMUNITY_LIST_VEEVA_ONLY_SEARCH_KEY)

  const {
    loadingAll,
    errorAll,
    loadingMine,
    errorMine,
    loadingRecommended,
    errorRecommended,
    allCommunitiesTotal,
    myCommunitiesTotal,
    veevaOnlyCommunitiesTotal,
    allCommunities,
    myCommunities,
    recommendedCommunities,
    veevaOnlyCommunities,
    searchVeevaOnly,
    loadingVeevaOnly,
    errorVeevaOnly,
    searchAll,
    searchMine,
    loadRecommended,
    unjoinedCommunities,
  } = useCommunities()
  const { companyId } = useAuth()

  const showMyCommunities = communityListTab === CommunityListTab.My
  const showAllCommunities =
    communityListTab === CommunityListTab.All ||
    (communityListTab === CommunityListTab.Recommended && !recommendedCommunities?.length)
  const showRecommendedCommunities =
    communityListTab === CommunityListTab.Recommended && Boolean(recommendedCommunities?.length)
  const showVeevaOnlyCommunities = communityListTab === CommunityListTab.VeevaOnly && isVeevan
  const loading = showMyCommunities
    ? loadingMine
    : showAllCommunities
      ? loadingAll
      : showRecommendedCommunities
        ? loadingRecommended
        : loadingVeevaOnly

  useEffect(() => {
    const main = document.querySelector('.main')
    const saveState = () => {
      if (main?.scrollTop) {
        localStorage.setItem(LocalStorageKey.communitiesScrollHeight, main?.scrollTop.toString())
      }
    }
    main?.addEventListener('scroll', saveState)
    return () => main?.removeEventListener('scroll', saveState)
  }, [])

  useEffect(() => {
    if (!scrolled && !loading) {
      document
        .querySelector('.main')
        ?.scrollTo({ top: Number.parseInt(localStorage.getItem(LocalStorageKey.communitiesScrollHeight) ?? '0') })
      setScrolled(true)
    }
  }, [loading, scrolled])

  useEffect(() => {
    localStorage.setItem(LocalStorageKey.displayedCountMine, String(displayedCountMine))
    localStorage.setItem(LocalStorageKey.displayedCountAll, String(displayedCountAll))
    localStorage.setItem(LocalStorageKey.displayedCountVeevaOnly, String(displayedCountVeevaOnly))
  }, [displayedCountAll, displayedCountMine, displayedCountVeevaOnly])

  const ascendingSort =
    (showAllCommunities && sortAscAll) ||
    (showMyCommunities && sortAscMine) ||
    (showVeevaOnlyCommunities && sortAscVeevaOnly)
  const sortingName =
    (showAllCommunities && sortTypeAll == CommunitySortType.NAME) ||
    (showMyCommunities && sortTypeMine == CommunitySortType.NAME) ||
    (showVeevaOnlyCommunities && sortTypeVeevaOnly == CommunitySortType.NAME)
  const sortingType =
    (showAllCommunities && sortTypeAll == CommunitySortType.TYPE) ||
    (showMyCommunities && sortTypeMine == CommunitySortType.TYPE)
  const sortingActivity =
    (showAllCommunities && sortTypeAll == CommunitySortType.ACTIVITY) ||
    (showMyCommunities && sortTypeMine == CommunitySortType.ACTIVITY) ||
    (showVeevaOnlyCommunities && sortTypeVeevaOnly == CommunitySortType.ACTIVITY)
  const sortingMembers =
    (showAllCommunities && sortTypeAll == CommunitySortType.MEMBERS) ||
    (showMyCommunities && sortTypeMine == CommunitySortType.MEMBERS) ||
    (showVeevaOnlyCommunities && sortTypeVeevaOnly == CommunitySortType.MEMBERS)
  const sortingAction = showAllCommunities && sortTypeAll == CommunitySortType.ACTION

  const sortOnJoin = useCallback(() => {
    const joined = myCommunities?.sort(nameCompare)
    const unjoined = unjoinedCommunities?.sort(nameCompare)
    return sortAscAll
      ? joined?.filter(comm => !comm.companyId).concat(unjoined ?? [])
      : unjoined?.concat(joined?.filter(comm => !comm.companyId) ?? [])
  }, [myCommunities, sortAscAll, unjoinedCommunities])

  const sortActivity = useCallback((asc: boolean, comms: CommunityListViewModel[]) => {
    function sortNulls(asc: boolean) {
      return function (a: CommunityListViewModel, b: CommunityListViewModel) {
        const a1 = a.lastActivityTime ? a.lastActivityTime.valueOf() : ''
        const b1 = b.lastActivityTime ? b.lastActivityTime.valueOf() : ''
        if (asc) {
          return a1 == b1 ? a.name.localeCompare(b.name) : a1 > b1 ? 1 : -1
        }
        return a1 == b1 ? a.name.localeCompare(b.name) : a1 > b1 ? -1 : 1
      }
    }

    return comms?.sort(sortNulls(asc))
  }, [])

  const sortComms = useCallback(
    (sortType: CommunitySortType, sortAsc: boolean, comms: CommunityListViewModel[]) => {
      let sortedComms = comms

      switch (sortType) {
        case CommunitySortType.NAME: {
          if (sortAsc) sortedComms?.sort(nameCompare)
          else sortedComms?.sort((a, b) => b.name.localeCompare(a.name))
          break
        }
        case CommunitySortType.TYPE: {
          if (sortAsc) sortedComms?.sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name))
          else sortedComms?.sort((a, b) => b.type.localeCompare(a.type) || a.name.localeCompare(b.name))
          break
        }
        case CommunitySortType.MEMBERS: {
          if (sortAsc)
            sortedComms?.sort((a, b) => (a.memberCount ?? 0) - (b.memberCount ?? 0) || a.name.localeCompare(b.name))
          else sortedComms?.sort((a, b) => (b.memberCount ?? 0) - (a.memberCount ?? 0) || a.name.localeCompare(b.name))
          break
        }
        case CommunitySortType.ACTIVITY: {
          sortedComms = sortActivity(sortAsc, sortedComms)
          break
        }
        default: {
          break
        }
      }

      return sortedComms
    },
    [sortActivity]
  )

  const communitiesToShow = useMemo(() => {
    const comms = showMyCommunities
      ? myCommunities
      : showAllCommunities
        ? sortTypeAll == CommunitySortType.ACTION && unjoinedCommunities && myCommunities
          ? sortOnJoin()
          : allCommunities
        : showVeevaOnlyCommunities
          ? veevaOnlyCommunities
          : recommendedCommunities

    return sortComms(
      showAllCommunities ? sortTypeAll : showMyCommunities ? sortTypeMine : sortTypeVeevaOnly,
      showAllCommunities ? sortAscAll : showMyCommunities ? sortAscMine : sortAscVeevaOnly,
      comms ?? []
    )
  }, [
    allCommunities,
    myCommunities,
    veevaOnlyCommunities,
    sortAscVeevaOnly,
    sortTypeVeevaOnly,
    recommendedCommunities,
    showAllCommunities,
    showMyCommunities,
    showVeevaOnlyCommunities,
    sortAscAll,
    sortAscMine,
    sortComms,
    sortOnJoin,
    sortTypeAll,
    sortTypeMine,
    unjoinedCommunities,
  ])

  const sortClicked = (sortOption: CommunitySortType) => {
    const sortAscMineTemp =
      sortOption == sortTypeMine
        ? !sortAscMine
        : sortOption != CommunitySortType.TYPE && sortOption != CommunitySortType.ACTIVITY
    const sortAscAllTemp =
      sortOption == sortTypeAll
        ? !sortAscAll
        : sortOption != CommunitySortType.TYPE && sortOption != CommunitySortType.ACTIVITY
    const sortAscVeevaOnlyTemp =
      sortOption == sortTypeVeevaOnly
        ? !sortAscVeevaOnly
        : sortOption != CommunitySortType.TYPE && sortOption != CommunitySortType.ACTIVITY

    if (showAllCommunities) {
      setDisplayedCountAll(25)
      setSortTypeAll(sortOption)
      setSortAscAll(sortAscAllTemp)
    } else if (showMyCommunities) {
      setDisplayedCountMine(25)
      setSortTypeMine(sortOption)
      setSortAscMine(sortAscMineTemp)
    } else {
      setDisplayedCountVeevaOnly(25)
      setSortTypeVeevaOnly(sortOption)
      setSortAscVeevaOnly(sortAscVeevaOnlyTemp)
    }
  }

  const loadMoreMine = () => {
    setDisplayedCountMine(displayedCountMine + 25)
  }
  const loadMoreAll = () => {
    setDisplayedCountAll(displayedCountAll + 25)
  }
  const loadMoreVeevaOnly = () => {
    setDisplayedCountVeevaOnly(i => i + 25)
  }

  const hasMoreMine = (myCommunities?.length ?? 0) > displayedCountMine
  const hasMoreAll = (allCommunities?.length ?? 0) > displayedCountAll
  const hasMoreVeevaOnly = (veevaOnlyCommunities?.length ?? 0) > displayedCountVeevaOnly

  const error = showMyCommunities
    ? errorMine
    : showAllCommunities
      ? errorAll
      : showRecommendedCommunities
        ? errorRecommended
        : errorVeevaOnly
  const defaultSearch = showMyCommunities ? mySearchText : showAllCommunities ? allSearchText : veevaOnlySearchText

  const showSearchClear =
    (mySearchText !== '' && showMyCommunities) ||
    (allSearchText !== '' && showAllCommunities) ||
    (veevaOnlySearchText !== '' && showVeevaOnlyCommunities)

  const debouncedSearchLogger = useMemo(() => debounce(searchQueryLogger, 300), [])

  const clearSearch = () => {
    if (showMyCommunities) {
      setMySearchText('')
      searchMine('')
    } else if (showAllCommunities) {
      setAllSearchText('')
      searchAll('')
    } else if (showVeevaOnlyCommunities) {
      setVeevaOnlySearchText('')
      searchVeevaOnly('')
    }
  }

  const getToast = useCallback((commName: string) => {
    const key = `${commName}-${Date.now()}`
    return (
      <ToastComponent show={true} onClose={() => handleCloseToast(key)} key={key} className={'recommended-toast'}>
        {`You've joined ${commName}.`}
      </ToastComponent>
    )
  }, [])

  const handleCloseToast = (key: string) => {
    setToastElements(prevElements => {
      for (let i = 0; i < prevElements.length; i++) {
        if (prevElements[i].key == key) {
          prevElements.splice(i, 1)
          return [...prevElements]
        }
      }
      return prevElements
    })
  }

  const handleJoin = (comm: CommunityListViewModel, action: MembershipAction) => {
    if (action == MembershipAction.join) {
      setToastElements(prevElements => prevElements.concat(getToast(comm.name)))
    }
  }

  const handleSearchTextChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchText = e.target.value
      if (showMyCommunities) {
        setMySearchText(searchText)
        searchMine(searchText)
      } else if (showAllCommunities) {
        setAllSearchText(searchText)
        searchAll(searchText)
      } else if (showVeevaOnlyCommunities) {
        setVeevaOnlySearchText(searchText)
        searchVeevaOnly(searchText)
      }
      void debouncedSearchLogger(searchText, 'community-search')
    },
    [
      showMyCommunities,
      showAllCommunities,
      showVeevaOnlyCommunities,
      setVeevaOnlySearchText,
      searchVeevaOnly,
      setMySearchText,
      setAllSearchText,
      searchMine,
      searchAll,
      debouncedSearchLogger,
    ]
  )

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // On refetch, load both lists using the current search text
  const refetch = useCallback(() => {
    searchMine(mySearchText)
    searchAll(allSearchText)
    searchVeevaOnly(veevaOnlySearchText)
    loadRecommended()
  }, [mySearchText, allSearchText, veevaOnlySearchText, searchVeevaOnly, searchMine, searchAll, loadRecommended])

  if (!communitiesToShow?.length && loading) return <div>loading...</div>
  if (error) return <Alert variant="danger">{errorMine?.message}</Alert>

  return (
    <div className={'table-list'} role="list">
      {!showRecommendedCommunities && (
        <div className={`searchbar${isCondensedPortrait ? ' condensed' : ''}`}>
          <img className="search-icon" src={searchIcon} alt={'Search'} />
          <Form.Control
            type="text"
            placeholder={showMyCommunities ? 'Search My Communities' : 'Search Communities'}
            value={defaultSearch}
            onChange={handleSearchTextChanged}
            role="searchBar"
            bsPrefix={' '}
          />
          {showSearchClear && (
            <img className={'search-clear'} src={searchClear} alt={'Clear'} title={'Clear'} onClick={clearSearch} />
          )}
        </div>
      )}
      {!isCondensedPortrait && (
        <div className="table-title communities">
          <span className={`wide-column ${isCondensedPortrait ? 'condensed' : ''}`}>
            <span
              className={`${showRecommendedCommunities ? '' : 'actions'}`}
              onClick={() => {
                if (!showRecommendedCommunities) sortClicked(CommunitySortType.NAME)
              }}
            >
              <span>Name & Description</span>
              <img
                src={moreIcon}
                alt={`Sort carrot${ascendingSort ? ' ascending' : ' descending'}`}
                className={`${sortingName ? 'sorting' : ''}${ascendingSort ? ' asc' : ' '}`}
              />
            </span>
          </span>
          <span className="normal-column">
            <span
              className={`${showRecommendedCommunities ? '' : 'actions'}`}
              onClick={() => {
                if (!showRecommendedCommunities) sortClicked(CommunitySortType.TYPE)
              }}
            >
              <span>Type</span>
              <img
                src={moreIcon}
                alt={'Carrot icon'}
                className={`${sortingType ? 'sorting' : ''}${ascendingSort ? '' : ' asc'}`} // Arrow is switched for type
              />
            </span>
          </span>
          <span className="normal-column">
            <span
              className={`${showRecommendedCommunities ? '' : 'actions'}`}
              onClick={() => {
                if (!showRecommendedCommunities) sortClicked(CommunitySortType.MEMBERS)
              }}
            >
              <span>Members</span>
              <img
                src={moreIcon}
                alt={`Sort carrot${ascendingSort ? ' ascending' : ' descending'}`}
                className={`${sortingMembers ? 'sorting' : ''}${ascendingSort ? ' asc' : ''}`}
              />
            </span>
          </span>
          <span className="normal-column">
            <span
              className={`${showRecommendedCommunities ? '' : 'actions'}`}
              onClick={() => {
                if (!showRecommendedCommunities) sortClicked(CommunitySortType.ACTIVITY)
              }}
            >
              <span>Last Activity</span>
              <img
                src={moreIcon}
                alt={`Sort carrot${ascendingSort ? ' ascending' : ' descending'}`}
                className={`${sortingActivity ? 'sorting' : ''}${ascendingSort ? ' asc' : ''}`}
              />
            </span>
          </span>
          <span className="normal-column end-aligned">
            <span className="max-width">
              <span
                className={`${showAllCommunities ? 'actions' : ''}`}
                onClick={() => {
                  if (showAllCommunities) sortClicked(CommunitySortType.ACTION)
                }}
              >
                <span>Action</span>
                <img
                  src={moreIcon}
                  alt={`Sort carrot${ascendingSort ? ' ascending' : ' descending'}`}
                  className={`${sortingAction ? 'sorting' : ''}${ascendingSort ? ' asc' : ''}`}
                />
              </span>
            </span>
          </span>
        </div>
      )}
      {communitiesToShow?.length ? (
        <div className={'content-region communities-list'}>
          {showMyCommunities && sortTypeMine == CommunitySortType.CUSTOM
            ? sortCommunityList(companyId ?? null, communitiesToShow)
                ?.slice(0, displayedCountMine)
                .map(community => (
                  <CommunityListRow
                    key={community.companyId || community.communityId}
                    community={community}
                    refetch={refetch}
                    logJoinClicked={(action, e) => logJoinClicked(community, action, e as SyntheticEvent)}
                    onActionCompleted={action => handleJoin(community, action)}
                  />
                ))
            : showRecommendedCommunities
              ? communitiesToShow?.map(community => (
                  <CommunityListRow
                    community={community}
                    key={community.companyId || community.communityId}
                    refetch={refetch}
                    logJoinClicked={(action, e) => logJoinClicked(community, action, e as SyntheticEvent)}
                    onActionCompleted={action => handleJoin(community, action)}
                  />
                ))
              : communitiesToShow
                  ?.slice(
                    0,
                    showAllCommunities
                      ? displayedCountAll
                      : showVeevaOnlyCommunities
                        ? displayedCountVeevaOnly
                        : displayedCountMine
                  )
                  .map(community => (
                    <CommunityListRow
                      community={community}
                      key={community.companyId || community.communityId}
                      refetch={refetch}
                      logJoinClicked={(action, e) => logJoinClicked(community, action, e as SyntheticEvent)}
                      onActionCompleted={action => handleJoin(community, action)}
                    />
                  ))}
        </div>
      ) : (
        <div className={'empty-section'}>No results found</div>
      )}
      <div className={'button-zone load-more'}>
        {showMyCommunities && hasMoreMine && (
          <button className={'btn btn-primary btn-sm'} onClick={loadMoreMine}>
            <strong>Show more</strong> {`(showing ${displayedCountMine} of ${myCommunitiesTotal})`}
          </button>
        )}
        {showAllCommunities && hasMoreAll && (
          <button className={'btn btn-primary btn-sm'} onClick={loadMoreAll}>
            <strong>Show more</strong> {`(showing ${displayedCountAll} of ${allCommunitiesTotal})`}
          </button>
        )}
        {showVeevaOnlyCommunities && hasMoreVeevaOnly && (
          <button className={'btn btn-primary btn-sm'} onClick={loadMoreVeevaOnly}>
            <strong>Show more</strong> {`(showing ${displayedCountVeevaOnly} of ${veevaOnlyCommunitiesTotal})`}
          </button>
        )}
      </div>
      {toastElements.length > 0 && toastElements.map(e => e)}
    </div>
  )
}

export default CommunityList
